import { signal } from "@preact/signals-core";

export const currentPath = signal<string>(window.location.pathname);

/**
 * Bumped on every navigate/replaceRoute/popstate, including ones that change
 * only the query string (which leave currentPath untouched). Pages that read
 * query params (?task=, ?app=, ?create=1) watch this and re-read
 * window.location.search. A counter, not the search string, so returning to
 * a query the page had since cleared with history.replaceState still fires.
 */
export const routeTick = signal(0);

window.addEventListener("popstate", () => {
  currentPath.value = window.location.pathname;
  routeTick.value++;
});

export function navigate(to: string): void {
  const url = new URL(to, window.location.origin);
  if (
    url.pathname === window.location.pathname &&
    url.search === window.location.search
  ) {
    return;
  }
  const samePath = url.pathname === window.location.pathname;
  window.history.pushState(null, "", url.pathname + url.search + url.hash);
  currentPath.value = url.pathname;
  routeTick.value++;
  // A query-only change (opening a task dialog) keeps the scroll position.
  if (!samePath) window.scrollTo(0, 0);
}

/**
 * Swap the current history entry and update the route signal.
 *
 * Use this instead of calling history.replaceState directly when the pathname
 * changes: replaceState alone leaves currentPath stale, and navigate() cannot
 * recover because it early-returns once the URL already matches its target.
 * That mismatch renders the previous route's page under the new URL.
 */
export function replaceRoute(to: string): void {
  const url = new URL(to, window.location.origin);
  window.history.replaceState(null, "", url.pathname + url.search + url.hash);
  currentPath.value = url.pathname;
  routeTick.value++;
}

/** pattern "/projects/:id" → { id: value } (keys have no colon prefix) */
export function matchRoute(
  pattern: string,
  path: string,
): Record<string, string> | null {
  const pSegs = pattern.split("/").filter(Boolean);
  const aSegs = path.split("/").filter(Boolean);
  if (pSegs.length !== aSegs.length) return null;
  const params: Record<string, string> = {};
  for (let i = 0; i < pSegs.length; i++) {
    const p = pSegs[i];
    const a = aSegs[i];
    if (p.startsWith(":")) {
      try {
        params[p.slice(1)] = decodeURIComponent(a);
      } catch {
        return null;
      }
    } else if (p !== a) return null;
  }
  return params;
}
